import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, X, Compass } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const EMPTY = {
  nome: '', telefone: '', email: '', documento: '',
  especialidade: '', idiomas: '', observacoes: '', status: 'ativo',
};

const ESPECIALIDADES = [
  'Trekking', 'Montanhismo', 'Histórico-cultural', 'Ecoturismo',
  'Aventura radical', 'City tour', 'Guia geral',
];

export default function Guias() {
  const { user } = useAuth();
  const canWrite = user?.role !== 'financeiro';
  const [guias, setGuias] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchGuias = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/guias', { params: { search: search || undefined } });
      setGuias(data);
    } catch { toast.error('Erro ao carregar guias'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchGuias(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setModalOpen(true); };
  const openEdit = (g) => { setForm({ ...EMPTY, ...g }); setEditingId(g.id); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(EMPTY); };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/guias/${editingId}`, form);
        toast.success('Guia atualizado!');
      } else {
        await api.post('/guias', form);
        toast.success('Guia cadastrado!');
      }
      closeModal(); fetchGuias();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/guias/${deleteId}`);
      toast.success('Guia excluído'); setDeleteId(null); fetchGuias();
    } catch { toast.error('Erro ao excluir'); }
  };

  const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]";

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Guias de Turismo</h1>
          <p className="text-slate-500 text-sm mt-0.5">{guias.length} guia(s) cadastrado(s)</p>
        </div>
        {canWrite && (
          <button onClick={openCreate}
            className="flex items-center gap-2 text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
            style={{ background: 'var(--brand-secondary, #d72d21)' }}>
            <Plus className="w-4 h-4" /> Novo Guia
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchGuias()}
              placeholder="Buscar por nome, email, telefone, especialidade..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-[var(--brand-primary)]" />
          </div>
          <button onClick={fetchGuias}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors"
            style={{ background: 'var(--brand-primary, #0147a0)' }}>
            Buscar
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : guias.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Compass className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhum guia cadastrado</p>
            <p className="text-xs mt-1">Clique em "Novo Guia" para começar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Especialidade</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Telefone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Idiomas</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {guias.map((g) => (
                  <tr key={g.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-slate-800">{g.nome}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden sm:table-cell">{g.especialidade || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden md:table-cell">{g.telefone || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell">{g.idiomas || '—'}</td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${g.status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {g.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        {canWrite && (
                          <>
                            <button onClick={() => openEdit(g)} title="Editar"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => setDeleteId(g.id)} title="Excluir"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg">{editingId ? 'Editar Guia' : 'Novo Guia'}</h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nome completo *</label>
                <input value={form.nome} onChange={e => f('nome', e.target.value)} required autoFocus className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone</label>
                  <input value={form.telefone || ''} onChange={e => f('telefone', e.target.value)} placeholder="(11) 99999-9999" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Documento (CPF)</label>
                  <input value={form.documento || ''} onChange={e => f('documento', e.target.value)} className={inp} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
                <input type="email" value={form.email || ''} onChange={e => f('email', e.target.value)} className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Especialidade</label>
                  <select value={form.especialidade || ''} onChange={e => f('especialidade', e.target.value)} className={inp + ' bg-white'}>
                    <option value="">— Não definida</option>
                    {ESPECIALIDADES.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select value={form.status} onChange={e => f('status', e.target.value)} className={inp + ' bg-white'}>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Idiomas</label>
                <input value={form.idiomas || ''} onChange={e => f('idiomas', e.target.value)} placeholder="Ex: Português, Inglês, Espanhol" className={inp} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
                <textarea value={form.observacoes || ''} onChange={e => f('observacoes', e.target.value)} rows={2} className={inp + ' resize-none'} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-50 transition-colors"
                  style={{ background: 'var(--brand-secondary, #d72d21)' }}>
                  {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2">Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
